import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { connect, useSelector } from "react-redux";
import "components/screenshot/screenshot.css";
import { Viewer } from "@photo-sphere-viewer/core";
import { VirtualTourPlugin } from "@photo-sphere-viewer/virtual-tour-plugin";
import { GalleryPlugin } from "@photo-sphere-viewer/gallery-plugin";

// CSS dos plugins
import "@photo-sphere-viewer/core/index.css";
import "@photo-sphere-viewer/markers-plugin/index.css";
import "@photo-sphere-viewer/compass-plugin/index.css";
import "@photo-sphere-viewer/gallery-plugin/index.css";
import "@photo-sphere-viewer/virtual-tour-plugin/index.css";

// Componentes, Servicos e Configuracoes
import DraggableLeafletMinimap from "./components/minimap";
import { Screenshot } from "./components/screenshot";
import { ReportList } from "./components/report";
import { viewerConfig } from "./viewerConfig";
import { virtualTourPluginConfig } from "./plugins/virtualTourPluginConfig";
import { updateMarkers } from "./plugins/markersService";
import { calculateBearing } from "./utils/calculateBearing";
import useImageLoader from "./useImageLoader";
import { useGyroscopePermission } from "./useGyroscopePermission";
import useAllProjectFiles from "hooks/useAllProjectFiles";
import ComparePanel from "components/compare/ComparePanel";
import "./VirtualTour.css";

function VirtualTourViewer({ email, token, isPublic = false }) {
  const [viewer, setViewer] = useState(null);
  const [activeMarkerIndex, setActiveMarkerIndex] = useState(0);
  const [loadingImage, setLoadingImage] = useState(true);
  const [activeModal, setActiveModal] = useState(null);

  // Compare state
  const [compareMode, setCompareMode] = useState(false);
  const [compareDateA, setCompareDateA] = useState(0);
  const [compareDateB, setCompareDateB] = useState(1);
  const viewerARef = useRef(null);
  const viewerBRef = useRef(null);
  const containerARef = useRef(null);
  const containerBRef = useRef(null);
  const syncingRef = useRef(false);

  const containerRef = useRef(null);
  const screenshotRef = useRef(null);
  const reportListRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const { project, date, folder_path: folderPathFromState } = isPublic ? {} : location.state || {};
  const name = project?.name || "";
  const folder_path = isPublic ? "" : (folderPathFromState || `projetos/${name}/${date}/tour_360`);

  const allReports = useSelector(state => state.report?.report || []);
  const prevReportCountRef = useRef(allReports.length);
  const [showCaptureHint, setShowCaptureHint] = useState(false);
  const [hintLeaving, setHintLeaving] = useState(false);
  const [hintStyle, setHintStyle] = useState({});
  const hintTimerRef = useRef(null);

  // Buscar todas as datas com tour_360
  const { allDates: allTourDates } = useAllProjectFiles(
    isPublic ? null : token,
    isPublic ? null : name,
    "tour_360"
  );
  const canCompare = allTourDates.length >= 2;

  const dismissHint = () => {
    setHintLeaving(true);
    setTimeout(() => { setShowCaptureHint(false); setHintLeaving(false); }, 260);
  };

  useEffect(() => {
    if (allReports.length > prevReportCountRef.current) {
      const reportBtn = containerRef.current?.querySelector('[title="Visualizar Ocorrências"]');
      if (reportBtn) {
        const rect = reportBtn.getBoundingClientRect();
        setHintStyle({
          position: "fixed",
          bottom: window.innerHeight - rect.top + 10,
          left: Math.max(8, rect.left + rect.width / 2 - 128),
          top: "auto",
          right: "auto"
        });
      } else {
        setHintStyle({
          position: "fixed",
          bottom: 80,
          left: "50%",
          transform: "translateX(-50%)",
          top: "auto",
          right: "auto"
        });
      }
      if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
      setShowCaptureHint(true);
      setHintLeaving(false);
      hintTimerRef.current = setTimeout(dismissHint, 5000);
    }
    prevReportCountRef.current = allReports.length;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allReports.length]);

  useEffect(() => {
    return () => { if (hintTimerRef.current) clearTimeout(hintTimerRef.current); };
  }, []);

  const getShareToken = () => {
    if (!isPublic) return null;
    const search = new URLSearchParams(location.search);
    return search.get("token");
  };

  const { images } = useImageLoader(
    isPublic
      ? { token: getShareToken() }
      : { email, name, date, token, folder_path }
  );

  const { permissionNeeded, requestPermission } = useGyroscopePermission();

  // Helper: criar config de viewer para um pane (sem compare button)
  const buildViewerConfig = useCallback((includeCompareBtn) => {
    const config = { ...viewerConfig, navbar: [...viewerConfig.navbar], plugins: [...viewerConfig.plugins] };

    if (!isPublic) {
      if (!config.navbar.some(btn => btn && btn.id === "screenshot-id")) {
        config.navbar.push({
          id: "screenshot-id",
          content: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>',
          title: "Screenshot / Ocorrência",
          className: "custom-button",
          onClick() {
            screenshotRef.current?.openScreenshotModal();
          }
        });
      }

      if (!config.navbar.some(btn => btn && btn.id === "report-button")) {
        config.navbar.push({
          id: "report-button",
          content: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>',
          title: "Visualizar Ocorrências",
          className: "custom-button",
          onClick: () => {
            reportListRef.current?.abrirReportList();
          }
        });
      }

      if (includeCompareBtn && !config.navbar.some(btn => btn && btn.id === "compare-btn")) {
        config.navbar.push({
          id: "compare-btn",
          content: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/><polyline points="15 7 18 4 21 7"/><polyline points="3 17 6 14 9 17"/></svg>',
          title: canCompare ? "Comparar datas" : "Comparação indisponível (necessário 2+ datas)",
          className: `custom-button${canCompare ? "" : " custom-button--disabled"}`,
          onClick: () => {
            if (canCompare) setCompareMode(prev => !prev);
          }
        });
      }
    }

    return config;
  }, [isPublic, canCompare]);

  // Helper: configurar um viewer PSV com imagens
  const setupViewer = useCallback((instance, imgs, handleMarkerClick) => {
    const isVirtualTour = imgs[0]?.hasOwnProperty("latitude");

    instance.addEventListener("ready", () => {
      setLoadingImage(false);

      // Traduzir tooltips dos botoes nativos do PSV para pt-BR
      requestAnimationFrame(() => {
        const container = instance.container;
        if (!container) return;
        const translations = {
          "Zoom in": "Aumentar zoom",
          "Zoom out": "Diminuir zoom",
          "Move up": "Mover para cima",
          "Move down": "Mover para baixo",
          "Move left": "Mover para esquerda",
          "Move right": "Mover para direita",
          "Fullscreen": "Tela cheia",
          "Gallery": "Galeria",
          "Gyroscope": "Giroscópio",
          "Toggle fullscreen": "Tela cheia",
          "Enable gyroscope": "Ativar giroscópio",
          "Disable gyroscope": "Desativar giroscópio"
        };
        container.querySelectorAll(".psv-button[title], .psv-custom-button[title]").forEach(btn => {
          const t = translations[btn.getAttribute("title")];
          if (t) btn.setAttribute("title", t);
        });
      });

      const scheduleResize = () => {
        requestAnimationFrame(() => {
          if (instance && typeof instance.autoSize === "function") {
            instance.autoSize();
          }
        });
      };
      scheduleResize();
      setTimeout(scheduleResize, 100);
      setTimeout(scheduleResize, 400);

      const initialSceneName = imgs[0]?.fileName || "default";
      updateMarkers(instance, initialSceneName, allReports, handleMarkerClick);

      const gallery = instance.getPlugin(GalleryPlugin);
      if (gallery) {
        gallery.setItems(
          imgs.map(img => ({
            id: img.fileName,
            panorama: img.src,
            thumbnail: img.src,
            name: img.fileName
          }))
        );
      }

      if (isVirtualTour) {
        const virtualTour = instance.getPlugin(VirtualTourPlugin);
        const nodes = imgs.map((img, index) => {
          const links = [];
          if (index + 1 < imgs.length) {
            const nextImg = imgs[index + 1];
            links.push({
              nodeId: `${index + 2}`,
              position: {
                yaw: calculateBearing(img.latitude, img.longitude, nextImg.latitude, nextImg.longitude),
                pitch: -0.2
              }
            });
          }
          if (index > 0) {
            const prevImg = imgs[index - 1];
            links.push({
              nodeId: `${index}`,
              position: {
                yaw: calculateBearing(img.latitude, img.longitude, prevImg.latitude, prevImg.longitude),
                pitch: -0.2
              }
            });
          }
          return {
            id: `${index + 1}`,
            panorama: img.src,
            name: img.fileName,
            links
          };
        });

        virtualTour.setNodes(nodes, "1");

        virtualTour.addEventListener("node-changed", ({ node }) => {
          const newIndex = parseInt(node.id, 10) - 1;
          setActiveMarkerIndex(newIndex);
          const newSceneName = imgs[newIndex]?.fileName || "default";
          updateMarkers(instance, newSceneName, allReports, handleMarkerClick);
        });
      } else {
        instance.setPanorama(imgs[0].src);
      }
    });
  }, [allReports]);

  // Modo normal: criar viewer unico
  useEffect(() => {
    if (compareMode) return;
    let instance = null;

    document.body.style.setProperty("overflow", "hidden", "important");

    if (images.length > 0 && containerRef.current) {
      const isVirtualTour = images[0].hasOwnProperty("latitude");
      const config = buildViewerConfig(true);

      if (isVirtualTour) {
        config.plugins = [...config.plugins, [VirtualTourPlugin, virtualTourPluginConfig]];
      }

      instance = new Viewer({
        container: containerRef.current,
        panorama: images[0].src,
        caption: `${name} <b>&copy; Vertex 4D</b>`,
        ...config
      });

      setViewer(instance);

      const handleMarkerClick = () => {
        reportListRef.current?.abrirReportList();
      };
      setupViewer(instance, images, handleMarkerClick);
    }

    return () => {
      if (instance) instance.destroy();
      document.body.style.setProperty("overflow", "auto", "important");
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images, allReports, isPublic, name, compareMode]);

  // Modo compare: criar dois viewers lado a lado
  useEffect(() => {
    if (!compareMode || allTourDates.length < 2) return;

    document.body.style.setProperty("overflow", "hidden", "important");

    const dateA = allTourDates[compareDateA];
    const dateB = allTourDates[compareDateB];
    if (!dateA || !dateB) return;

    // Converter files de cada date em formato de imagens (mesmo que useImageLoader)
    const filesToImages = (files) =>
      files.map(file => ({
        src: file.url,
        fileName: file.url.substring(file.url.lastIndexOf("/") + 1),
        ...(file.gps && {
          latitude: file.gps.latitude_ref === "S" || file.gps.latitude_ref === "W"
            ? -Math.abs(file.gps.latitude)
            : Math.abs(file.gps.latitude),
          longitude: file.gps.longitude_ref === "S" || file.gps.longitude_ref === "W"
            ? -Math.abs(file.gps.longitude)
            : Math.abs(file.gps.longitude)
        })
      }));

    const imgsA = filesToImages(dateA.files);
    const imgsB = filesToImages(dateB.files);

    if (imgsA.length === 0 || imgsB.length === 0) return;

    // Viewer config minimalista para panes de compare (sem screenshot/report buttons)
    const makeMinimalConfig = () => {
      const cfg = { ...viewerConfig, navbar: ["zoom", "move", "caption", "fullscreen"], plugins: [...viewerConfig.plugins] };
      const isVT = imgsA[0]?.hasOwnProperty("latitude");
      if (isVT) {
        cfg.plugins = [...cfg.plugins, [VirtualTourPlugin, virtualTourPluginConfig]];
      }
      return cfg;
    };

    let instanceA = null;
    let instanceB = null;

    if (containerARef.current && containerBRef.current) {
      const cfgA = makeMinimalConfig();
      const cfgB = makeMinimalConfig();

      instanceA = new Viewer({
        container: containerARef.current,
        panorama: imgsA[0].src,
        caption: `${dateA.dateLabel} <b>&copy; Vertex 4D</b>`,
        ...cfgA
      });

      instanceB = new Viewer({
        container: containerBRef.current,
        panorama: imgsB[0].src,
        caption: `${dateB.dateLabel} <b>&copy; Vertex 4D</b>`,
        ...cfgB
      });

      viewerARef.current = instanceA;
      viewerBRef.current = instanceB;
      setViewer(instanceA);

      const noopMarkerClick = () => {};
      setupViewer(instanceA, imgsA, noopMarkerClick);
      setupViewer(instanceB, imgsB, noopMarkerClick);

      // Sync camera entre A e B
      const syncAtoB = () => {
        if (syncingRef.current || !viewerBRef.current || !viewerARef.current) return;
        syncingRef.current = true;
        try {
          viewerBRef.current.rotate(viewerARef.current.getPosition());
          viewerBRef.current.zoom(viewerARef.current.getZoomLevel());
        } catch (_) {}
        syncingRef.current = false;
      };

      const syncBtoA = () => {
        if (syncingRef.current || !viewerARef.current || !viewerBRef.current) return;
        syncingRef.current = true;
        try {
          viewerARef.current.rotate(viewerBRef.current.getPosition());
          viewerARef.current.zoom(viewerBRef.current.getZoomLevel());
        } catch (_) {}
        syncingRef.current = false;
      };

      instanceA.addEventListener("position-updated", syncAtoB);
      instanceB.addEventListener("position-updated", syncBtoA);
    }

    return () => {
      if (instanceA) instanceA.destroy();
      if (instanceB) instanceB.destroy();
      viewerARef.current = null;
      viewerBRef.current = null;
      document.body.style.setProperty("overflow", "auto", "important");
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareMode, compareDateA, compareDateB, allTourDates]);

  const handleMapMarkerClick = index => {
    if (viewer) {
      if (viewer.getPlugin(VirtualTourPlugin)) {
        viewer.getPlugin(VirtualTourPlugin).setCurrentNode(`${index + 1}`);
      } else {
        viewer
          .setPanorama(images[index].src)
          .then(() => setActiveMarkerIndex(index));
      }
    }
  };

  const handleCloseTour = () => {
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/projects", {
        state: name ? { openProject: name } : undefined,
      });
    }
  };

  return (
    <div className={`viewer-page${compareMode ? " viewer-page--compare" : ""}`}>
      {!isPublic && (
        <div className="QuitButtonTour">
          <button type="button" title="Voltar" onClick={handleCloseTour}>&times;</button>
        </div>
      )}

      {/* Compare panel overlay */}
      {compareMode && canCompare && (
        <div className="tour-compare__overlay" style={{ zIndex: 20 }}>
          <ComparePanel
            items={allTourDates}
            indexA={compareDateA}
            indexB={compareDateB}
            onChangeA={setCompareDateA}
            onChangeB={setCompareDateB}
          />
          <button
            className="tour-compare__exit-btn"
            onClick={() => setCompareMode(false)}
            title="Sair do modo comparação"
          >
            ✕ Sair
          </button>
        </div>
      )}

      {/* Modo normal */}
      {!compareMode && (
        <div ref={containerRef} className="viewer-container">
          {loadingImage && <div className="loading">Carregando imagem...</div>}
        </div>
      )}

      {/* Modo compare: dois panes lado a lado */}
      {compareMode && (
        <div className="tour-compare__split">
          <div className="tour-compare__pane">
            <span className="tour-compare__pane-label">A</span>
            <div ref={containerARef} className="tour-compare__pane-viewer" />
          </div>
          <div className="tour-compare__pane">
            <span className="tour-compare__pane-label">B</span>
            <div ref={containerBRef} className="tour-compare__pane-viewer" />
          </div>
        </div>
      )}

      {permissionNeeded && (
        <button onClick={requestPermission} className="gyroscope-button">
          Ativar Giroscópio
        </button>
      )}

      {!compareMode && !loadingImage && images.length > 0 && isPublic === false && (
        <div className="overlay-ui">
          <DraggableLeafletMinimap
            images={images}
            onMarkerClick={handleMapMarkerClick}
            activeMarker={activeMarkerIndex}
          />
        </div>
      )}

      {!isPublic && viewer && !compareMode && (
        <>
          <Screenshot
            ref={screenshotRef}
            viewer={viewer}
            folderPath={folder_path}
          />
          <ReportList
            ref={reportListRef}
            folderPath={folder_path}
            token={token}
            onOpen={() => setActiveModal("report")}
            onClose={() => setActiveModal(null)}
            isActive={activeModal === "report"}
          />
        </>
      )}

      {showCaptureHint && createPortal(
        <div
          className={`capture-hint capture-hint--arrow-bottom${hintLeaving ? " capture-hint--leaving" : ""}`}
          style={{ ...hintStyle, "--hint-duration": "5000ms" }}
          onClick={dismissHint}
          role="status"
          aria-live="polite"
        >
          <div className="capture-hint__body">
            <div className="capture-hint__check">
              <i className="fas fa-check" />
            </div>
            <div className="capture-hint__text">
              <span className="capture-hint__title">Captura registrada</span>
              <span className="capture-hint__sub">
                Clique em <i className="fas fa-file-alt" /> para revisar e salvar
              </span>
            </div>
            <div className="capture-hint__arrow">
              <i className="fas fa-chevron-right" />
            </div>
          </div>
          <div className="capture-hint__timer">
            <div className="capture-hint__timer-fill" />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

const mapStateToProps = state => ({
  email: state.auth.email,
  token: state.auth.accessToken
});

export default connect(mapStateToProps)(VirtualTourViewer);
